import { GLOBAL_ASSETS_SENTINEL_OWNER_ID } from '@cadstrom/cadstrom-shared';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { GLOBAL_LABEL } from '#components/owner-organization-picker/owner-organization-picker.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { useOrganizationById } from '#utils/use-organization-by-id.js';

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId/general/')({
	staticData: {
		navigation: {
			label: 'Family: General',
			keywords: ['general', 'family', 'name'],
		},
	},
	loader: async ({ context: { queryClient, getPackageFamilyOptions } }) => {
		const { pkgFamily } = await queryClient.ensureQueryData(getPackageFamilyOptions);
		return { pkgFamily };
	},
	head: ({ loaderData }) => ({
		meta: [
			{ title: (loaderData ? `Family ${loaderData.pkgFamily.name}: ` : '') + 'General • Package Editor • Cadstrom' },
		],
	}),
	component: FamilyGeneralTab,
});

function FamilyGeneralTab() {
	const queryClient = useQueryClient();
	const trpc = useTRPC();

	const { getPackageFamilyOptions } = Route.useRouteContext();

	const {
		data: { pkgFamily },
	} = useSuspenseQuery(getPackageFamilyOptions);

	const isGlobal = pkgFamily.ownerId === GLOBAL_ASSETS_SENTINEL_OWNER_ID;
	const { displayName: ownerDisplayName } = useOrganizationById(isGlobal ? undefined : pkgFamily.ownerId);
	const ownerFieldValue = isGlobal ? GLOBAL_LABEL : (ownerDisplayName ?? 'Private organization');

	const { mutateAsync: updatePackageFamily, isPending: updatePackageFamilyIsPending } = useMutation(
		trpc.packageFamily.update.mutationOptions({
			onSuccess: (res) => {
				queryClient.setQueryData(getPackageFamilyOptions.queryKey, res);

				setServerPackageFamily({
					name: res.pkgFamily.name,
				});
			},
		}),
	);

	const {
		draftState: draftPackageFamily,
		setDraftState: setDraftPackageFamily,
		setServerState: setServerPackageFamily,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: {
			name: pkgFamily.name,
		},
		mutationKeys: trpc.packageFamily.update.mutationKey(),
		saveFn: async (draftPackageFamily) =>
			await updatePackageFamily({
				id: pkgFamily.id,
				data: {
					name: draftPackageFamily.name,
				},
			}),
	});

	const formLabel = (text: string, htmlFor: string) => (
		<label className='text-right text-gray-11' htmlFor={htmlFor}>
			{text}
		</label>
	);

	return (
		<div className='grow overflow-auto px-3 py-2' ref={saveAreaContainerRef}>
			<Button
				disabled={!isDirty}
				loading={updatePackageFamilyIsPending}
				startIcon={<SaveIcon />}
				className='float-right mb-2 ml-3'
				onClick={() => void handleSave()}
			>
				<p>Save</p>
			</Button>

			<div className='grid w-full max-w-2xl grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2'>
				{formLabel('Name', 'package-family-name')}
				<OutlinedInput
					id='package-family-name'
					value={draftPackageFamily.name}
					onChange={(event) => {
						setDraftPackageFamily((prev) => ({ ...prev, name: event.target.value }));
					}}
				/>

				{formLabel('Owner', 'package-family-owner')}
				<OutlinedInput
					id='package-family-owner'
					readOnly
					value={ownerFieldValue}
					inputProps={{ className: 'text-gray-a10' }}
					endAdornment={
						<InputAdornment position='end'>
							<LockIcon className='text-gray-a8' fontSize='small' />
						</InputAdornment>
					}
				/>
			</div>
		</div>
	);
}
